import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #rebuyOpenRetryTimeout;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#clearRebuyOpenRetry();
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!(event instanceof CartAddEvent)) return;

    const source = event?.detail?.data?.source;
    if (source === 'discount-kit-sync') return;

    this.open();
  };

  open() {
    if (this.#showRebuySmartCart()) {
      return;
    }

    if (this.#hasRebuyScript()) {
      this.#retryOpenRebuySmartCart();
      return;
    }

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #showRebuySmartCart() {
    if (typeof Rebuy === 'undefined') return false;
    if (typeof Rebuy.SmartCart?.show !== 'function') return false;

    Rebuy.SmartCart.show();
    return true;
  }

  #hasRebuyScript() {
    return !!document.querySelector('script[src*="cdn.rebuyengine.com/onsite/js/rebuy.js"]');
  }

  #clearRebuyOpenRetry() {
    if (this.#rebuyOpenRetryTimeout) {
      clearTimeout(this.#rebuyOpenRetryTimeout);
      this.#rebuyOpenRetryTimeout = undefined;
    }
  }

  #retryOpenRebuySmartCart = () => {
    this.#clearRebuyOpenRetry();

    const startedAt = Date.now();
    const retry = () => {
      if (this.#showRebuySmartCart()) {
        this.#clearRebuyOpenRetry();
        return;
      }

      if (Date.now() - startedAt >= 2000) {
        this.showDialog();
        this.#clearRebuyOpenRetry();
        return;
      }

      this.#rebuyOpenRetryTimeout = setTimeout(retry, 100);
    };

    retry();
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
